import {
  Button,
  Container,
  Flex,
  Image,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  Anchor,
  Alert,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import axios from "axios";
import { useLayoutEffect, useState } from "react";
import { URL } from "../../services/url";
import checkUserId from "../../utils/checkuserid";
import { IconAlertCircle, IconLock, IconMail, IconPhone } from "@tabler/icons-react";

interface SignUpResponse {
  success: boolean;
  response: string;
  user?: number;
  token?: string;
}

const Authentication = () => {
  const isMobile = useMediaQuery("(max-width: 600px)");

  const [email, setEmail] = useState("");
  const [invalidEmail, setInvalidemail] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [invalidphone, setInvalidPhone] = useState(false);

  const [password, setPassword] = useState("");
  const [passwordErr, setPasswordErr] = useState(false);

  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordErr, setConfirmPasswordErr] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useLayoutEffect(() => {
    checkUserId();
  }, []);

  const isFormValid =
    email &&
    phoneNumber &&
    password &&
    confirmPassword &&
    !invalidEmail &&
    !invalidphone &&
    !passwordErr &&
    !confirmPasswordErr;

  const handleSignUp = async (e: any) => {
    e.preventDefault();
    if (!isFormValid) {
      setError("Please fill all required fields");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await axios.post<SignUpResponse>(`${URL}signup/`, {
        email,
        phonenumber: phoneNumber,
        password,
      });

      if (response.data.success) {
        const userId = response.data.user;
        const token = response.data.token;
        // Store the user ID or any other relevant data in local storage or state management solution
        localStorage.setItem("userId", userId?.toString() || "");
        localStorage.setItem("token", token?.toString() || "");
        window.location.href = "/personal";
        // window.location.href = "/signin";
      } else {
        setError(response.data.response);
      }
      setLoading(false);
    } catch (error) {
      console.error("Sign up failed", error);
      setError("An error occurred during sign up");
      setLoading(false);
    }
  };

  const validatePassword = (e: any) => {
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[\w!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{8,}$/;
    if (passwordPattern.test(password) === false) {
      setError("Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character");
      setPasswordErr(true);
    } else {
      setError("");
      setPasswordErr(false);
    }
  };

  const onChangeConfirmPassword = (e: any) => {
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setConfirmPasswordErr(true);
    } else {
      setError("");
      setConfirmPasswordErr(false);
    }
  };

  const isInvalidPhonenumber = () => {
    const re = /^^(?:\+?61|0)[2-478]\d{8}$/;
    if (!re.test(phoneNumber)) {
      setInvalidPhone(true);
      setError("Invalid phone number");
    } else {
      setInvalidPhone(false);
      setError("");
    }
  };

  function validateEmail() {
    // Regular expression for email validation
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!regex.test(email)) {
      setInvalidemail(true);
      setError("Invalid email address");
    } else {
      setInvalidemail(false);
      setError("");
    }
  }

  return (
    <Container
      size={isMobile ? "xs" : "sm"}
      px={isMobile ? "md" : "xl"}
      py={isMobile ? "xl" : "80px"}
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Stack gap="xl" align="center" w="100%">
        {/* Header */}
        <Stack align="center" gap="md" ta="center">
          <Image 
            src="/landingimg.png" 
            maw={isMobile ? 120 : 150} 
          />
          <Stack gap="xs" align="center">
            <Title order={1} size={isMobile ? "h2" : "h1"} fw={700}>
              Kabbie
            </Title>
            <Text size="lg" c="dimmed">
              Driver Portal
            </Text>
          </Stack>
        </Stack>

        {/* Form */}
        <Stack gap="lg" w="100%" maw={400}>
          <Stack gap="xs" ta="center">
            <Title order={2} size="h3">
              Create Driver Account
            </Title>
            <Text size="sm" c="dimmed">
              Join our community of professional drivers
            </Text>
          </Stack>
              <TextInput
                label="Email Address"
                placeholder="driver@example.com"
                type="email"
                required
                size="md"
                leftSection={<IconMail size={18} />}
                value={email}
                error={invalidEmail}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                onBlur={validateEmail}
                styles={{
                  label: { marginBottom: "0.5rem" },
                }}
              />

              <TextInput
                label="Phone Number"
                placeholder="+61 4XX XXX XXX"
                type="tel"
                required
                size="md"
                leftSection={<IconPhone size={18} />}
                error={invalidphone}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onBlur={isInvalidPhonenumber}
                styles={{
                  label: { marginBottom: "0.5rem" },
                }}
              />

              <PasswordInput
                label="Password"
                placeholder="Create a strong password"
                required
                size="md"
                leftSection={<IconLock size={18} />}
                error={passwordErr}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocusCapture={(e) => setError("")}
                onBlurCapture={validatePassword}
                styles={{
                  label: { marginBottom: "0.5rem" },
                }}
              />

              <PasswordInput
                label="Confirm Password"
                placeholder="Re-enter your password"
                required
                error={confirmPasswordErr}
                size="md"
                leftSection={<IconLock size={18} />}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocusCapture={(e) => setError("")}
                onBlurCapture={onChangeConfirmPassword}
                styles={{
                  label: { marginBottom: "0.5rem" },
                }}
              />

              {error && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  variant="light"
                  radius="md"
                >
                  <Text size="sm">{error}</Text>
                </Alert>
              )}

          <Button
            fullWidth
            size="lg"
            mt="md"
            onClick={handleSignUp}
            variant="gradient"
            gradient={{ from: '#000000', to: '#333333', deg: 145 }}
            loading={loading}
            loaderProps={{ type: "oval" }}
            radius="md"
            disabled={!isFormValid}
          >
            Create Account
          </Button>

          <Stack gap="sm" mt="md" ta="center">
            <Anchor href="/" size="sm" c="dimmed">
              ← Back to Home
            </Anchor>
            <Text size="sm" c="dimmed">
              Already have an account?{" "}
              <Anchor href="/signin" fw={600}>
                Sign In
              </Anchor>
            </Text>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};

export default Authentication;
